// Hooks
import { useCurrentFinanceProject } from "../hooks/useCurrentFinanceProject"

// Analytics
import { sendEvent } from "@/lib/analytics"

// Router
import { useNavigate } from "@/lib/router"
import { Routes } from "@/constants/routes"

// Translations
import { useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

// UI
import { ProjectSelector } from "@/components/ProjectSelector"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"
import { Heading } from "@/components/Typography"
import { Card } from "@/components/Card"
import { Disclaimer } from "@/components/Disclaimer"
import { SolarInterestTariffGraph } from "@/components/graphs/types/area/SolarInterestTariffGraph"

import { FinanceProjectSubMenuTabs } from "../components/FinanceProjectSubMenuTabs"
import { SolarInterestTariffDownloads } from "./_components/SolarInterestTariffDownloads"

// Template
import { PageTemplate } from "@/layouts/PageTemplate"

// REST queries
import { useApiV1ComparisonProjectEndexPriceRetrieve } from "@/api/rest/generated/api/api"

// Graphs
import { FinanceGraphSolarInterestTariff } from "../components/FinanceGraphSolarInterestTariff"

/**
 * FinanceSolarInterestTariff
 * @returns
 */
export function FinanceSolarInterestTariff() {
	const currentProject = useCurrentFinanceProject().data.project
	const projectId = Number(currentProject?.id) || 1 // TODO: Pick a default
	const navigate = useNavigate()

	// Translate
	const t = useTrans("finance")
	const { formatCurrency } = useLang()

	// Rest Query
	const { data: dataEndexPrice } =
		useApiV1ComparisonProjectEndexPriceRetrieve(projectId)
	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: t(
						"finance:finance.solar_interest_tariff_with_project.title",
						{
							projectName: currentProject?.name,
						},
					),
				})}
			</title>
			<PageTemplate
				title={t(
					"finance:finance.solar_interest_tariff_with_project.title",
					{
						projectName: currentProject?.name,
					},
				)}
				topRightComponent={
					<ProjectSelector
						projectId={projectId}
						onChange={(id) => {
							sendEvent("finance", "performance_project_filter", {
								label: id,
							})
							navigate(
								`${Routes.FinanceSolarInterestTariff}/${id}`,
							)
						}}
					/>
				}
				backHref={Routes.FinanceProjects}
				tabs={<FinanceProjectSubMenuTabs />}
			>
				<Disclaimer
					className="mb-4"
					title={t(
						"finance:finance.solar_interest_tariff.block.info.title",
					)}
					message={
						<p className="whitespace-break-spaces">
							{t(
								"finance:finance.solar_interest_tariff.block.info.copy",
							)}
						</p>
					}
				/>

				<div className="flex flex-col gap-8 md:flex-row">
					<Card className="md:w-1/2">
						<Heading as="h3" styleAs="h6" className="mr-2 lg:mr-0">
							{t(
								"finance:finance.solar_interest_tariff.block.endex_tariffs.title",
							)}
						</Heading>

						<SolarInterestTariffGraph>
							<SolarInterestTariffGraph.Content>
								<FinanceGraphSolarInterestTariff />
							</SolarInterestTariffGraph.Content>
							<SolarInterestTariffGraph.Footer>
								<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
									<GraphLabel
										type="line"
										variant="primary"
										title={t(
											"finance:finance.solar_interest_tariff.block.endex_tariffs",
										)}
									/>
									<GraphLabel
										type="bar"
										variant="default"
										title={t(
											"finance:finance.solar_interest_tariff.block.average_tariffs",
										)}
									/>
								</GraphLabels>
							</SolarInterestTariffGraph.Footer>
						</SolarInterestTariffGraph>
					</Card>

					<div className="flex h-[600px] flex-col md:w-1/2">
						<Card>
							<Heading
								as="h3"
								styleAs="h6"
								className="mr-2 lg:mr-0"
							>
								Endex 12-0-12
							</Heading>

							{dataEndexPrice?.price ? (
								<>
									{`${dataEndexPrice?.start} - ${dataEndexPrice?.end}`}
									<div>
										{`${formatCurrency(
											dataEndexPrice?.price / 1000,
											{
												minimumFractionDigits: 6,
												maximumFractionDigits: 6,
											},
										)} per kWh`}
									</div>
								</>
							) : null}
						</Card>

						{/** Solar interest tariffs */}
						<SolarInterestTariffDownloads />
					</div>
				</div>
			</PageTemplate>
		</>
	)
}
